import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api";
import { track } from "../../lib/analytics";
import { AuthLayout } from "./LoginPage";

export function SignupPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    street: "",
    zip: "",
    city: "",
    country: "DE",
    phoneE164: "",
  });
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/api/auth/signup", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          phoneE164: form.phoneE164 || undefined,
          acceptTerms: accept,
        }),
      });
      track("signup");
      await qc.invalidateQueries({ queryKey: ["me"] });
      nav("/app");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setError("E-Mail bereits registriert");
      else setError(t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <AuthLayout title={t("auth.signupTitle")}>
      <form onSubmit={submit} className="space-y-3">
        <Field label={t("auth.name")}>
          <input className="input" required value={form.name} onChange={(e) => set("name", e.target.value)} />
        </Field>
        <Field label={t("auth.email")}>
          <input type="email" className="input" required value={form.email} onChange={(e) => set("email", e.target.value)} />
        </Field>
        <Field label={t("auth.password")}>
          <input type="password" className="input" required minLength={10} value={form.password} onChange={(e) => set("password", e.target.value)} />
        </Field>
        <Field label={t("auth.street")}>
          <input className="input" required value={form.street} onChange={(e) => set("street", e.target.value)} />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label={t("auth.zip")}>
            <input className="input" required value={form.zip} onChange={(e) => set("zip", e.target.value)} />
          </Field>
          <div className="col-span-2">
            <Field label={t("auth.city")}>
              <input className="input" required value={form.city} onChange={(e) => set("city", e.target.value)} />
            </Field>
          </div>
        </div>
        <Field label={t("auth.phone")} hint={t("auth.phoneHint")}>
          <input className="input" placeholder="+491701234567" value={form.phoneE164} onChange={(e) => set("phoneE164", e.target.value)} />
        </Field>

        <p className="text-xs text-slate-500 mt-2">{t("auth.privacyNote")}</p>

        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" required checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-1" />
          <span>
            {t("auth.acceptTerms")}{" "}
            <Link to="/agb" target="_blank" className="underline">AGB</Link>{" · "}
            <Link to="/datenschutz" target="_blank" className="underline">Datenschutz</Link>
          </span>
        </label>

        {error && <p className="text-red-600 text-sm">{error}</p>}

        <button disabled={busy || !accept} className="btn-primary w-full">{t("auth.submitSignup")}</button>
        <div className="text-sm text-slate-600 text-center">
          <Link to="/login" className="underline">{t("auth.haveAccount")}</Link>
        </div>
      </form>
    </AuthLayout>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}
